import { Store } from '@reduxjs/toolkit';

import { StoreSubscribeListener } from '../types';
import { createLinkedList } from '../utils';

const pendingCancels = new Set<() => void>();

/**
 * Откладывает вызов notify через микротаск с дедупликацией.
 * Множественные вызовы в одном синхронном потоке схлопываются в один отложенный вызов.
 *
 * @returns [deferred, cancel] — отложенный notify и функция отмены pending микротаска
 */
export function createDeferredNotify(notify: () => void): [deferred: () => void, cancel: () => void] {
    let pending = false;
    let cancelled = false;

    function cancel() {
        if (pending) {
            cancelled = true;
            pending = false;
            pendingCancels.delete(cancel);
        }
    }

    function deferred() {
        if (pending) return;
        pending = true;
        cancelled = false;
        pendingCancels.add(cancel);
        queueMicrotask(() => {
            pending = false;
            if (cancelled) {
                cancelled = false;
                return;
            }
            pendingCancels.delete(cancel);
            notify();
        });
    }

    return [deferred, cancel];
}

/**
 * Отменяет все pending deferred notify микротаски.
 * Вызывается из cleanupStores() для очистки между тестами.
 */
export function cleanupPendingNotifies() {
    for (const cancel of pendingCancels) {
        cancel();
    }
    pendingCancels.clear();
}

let batchDepth = 0;
const suspendedNotifies = new Set<() => void>();

/**
 * Схлопывает серию синхронных dispatch в одну нотификацию подписчиков bound-селекторов.
 * Нотификация остаётся синхронной — уходит в конце fn, в той же таске.
 */
export function batchSelectorNotifications<T>(fn: () => T): T {
    batchDepth++;
    try {
        return fn();
    } finally {
        batchDepth--;
        if (batchDepth === 0 && suspendedNotifies.size > 0) {
            const toNotify = [...suspendedNotifies];
            suspendedNotifies.clear();
            for (const notify of toNotify) {
                notify();
            }
        }
    }
}

function notifyOrDefer(notify: () => void) {
    if (batchDepth > 0) {
        suspendedNotifies.add(notify);
    } else {
        notify();
    }
}

// Все подписки из useSelector хуков хранятся в связном списке, чтобы иметь O(1) при отписке.
export function createListenerCollection() {
    const list = createLinkedList<StoreSubscribeListener>();

    return {
        notify() {
            let listener = list.head();

            while (listener) {
                // Записываем следующего слушателя перед вызовом текущего,
                // так как текущий может отписаться и listener.next станет null
                let nextListener = listener.next;

                listener.value();

                listener = nextListener;
            }
        },

        subscribe(callback: StoreSubscribeListener) {
            const listener = list.append(callback);

            return function unsubscribe() {
                list.remove(listener);
            };
        },
    };
}

/**
 * Создает функцию подписки на изменения стора с синхронной нотификацией подписчиков.
 *
 * @param store – Модульный стор.
 * @returns Функция подписки на изменения стора.
 */
export function createBatchedSubscription(store: Store) {
    const listeners = createListenerCollection();

    store.subscribe(() => notifyOrDefer(listeners.notify));

    return function addSubscription(listener: StoreSubscribeListener) {
        return listeners.subscribe(listener);
    };
}
